import axios from "axios";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import { useEffect, useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const bulanList = [
  { value: "", label: "Semua Bulan" },
  { value: "01", label: "Januari" },
  { value: "02", label: "Februari" },
  { value: "03", label: "Maret" },
  { value: "04", label: "April" },
  { value: "05", label: "Mei" },
  { value: "06", label: "Juni" },
  { value: "07", label: "Juli" },
  { value: "08", label: "Agustus" },
  { value: "09", label: "September" },
  { value: "10", label: "Oktober" },
  { value: "11", label: "November" },
  { value: "12", label: "Desember" },
];

const ProfileKepsek = () => {
  const [absensi, setAbsensi] = useState([]);
  const [tahun, setTahun] = useState("");
  const [semester, setSemester] = useState("");
  const [bulan, setBulan] = useState("");

  // ================= FETCH =================
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}api/absensi/getall`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then((res) => {
        setAbsensi(res.data);
        const years = res.data.map((d) => Number(d.tahun));
        if (years.length) setTahun(String(Math.max(...years)));
      });
  }, []);

  // ================= SEMESTER =================
  const isSemesterMatch = (month) => {
    if (!semester) return true;
    if (semester === "1") return month >= 7 && month <= 12;
    if (semester === "2") return month >= 1 && month <= 6;
    return true;
  };

  // ================= TOTAL =================
  const totalSekolah = useMemo(() => {
    let sakit = 0,
      izin = 0,
      tanpa = 0;

    absensi.forEach((a) => {
      if (a.tahun !== tahun) return;
      const m = new Date(a.tanggal).getMonth() + 1;
      const mStr = String(m).padStart(2, "0");
      if (bulan && bulan !== mStr) return;
      if (!isSemesterMatch(m)) return;

      a.siswa.forEach((s) => {
        sakit += s.sakit || 0;
        izin += s.izin || 0;
        tanpa += s.tanpa_keterangan || 0;
      });
    });

    return {
      sakit: Math.round(sakit),
      izin: Math.round(izin),
      tanpa: Math.round(tanpa),
    };
  }, [absensi, tahun, bulan, semester]);

  // ================= PER ROMBEL =================
  const perRombel = useMemo(() => {
    const map = {};

    absensi.forEach((a) => {
      if (a.tahun !== tahun) return;
      const m = new Date(a.tanggal).getMonth() + 1;
      const mStr = String(m).padStart(2, "0");
      if (bulan && bulan !== mStr) return;
      if (!isSemesterMatch(m)) return;

      const key = `${a.kelas}${a.rombel}`;
      if (!map[key]) {
        map[key] = {
          label: `Kelas ${a.kelas}${a.rombel}`,
          sakit: 0,
          izin: 0,
          tanpa: 0,
        };
      }

      a.siswa.forEach((s) => {
        map[key].sakit += s.sakit || 0;
        map[key].izin += s.izin || 0;
        map[key].tanpa += s.tanpa_keterangan || 0;
      });
    });

    Object.values(map).forEach((v) => {
      v.sakit = Math.round(v.sakit);
      v.izin = Math.round(v.izin);
      v.tanpa = Math.round(v.tanpa);
    });

    return map;
  }, [absensi, tahun, bulan, semester]);

  // ================= CHART =================
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: "top" } },
    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
  };

  const chartTotal = {
    labels: ["Sakit", "Izin", "Tanpa Keterangan"],
    datasets: [
      {
        label: "Total Sekolah",
        data: [totalSekolah.sakit, totalSekolah.izin, totalSekolah.tanpa],
        backgroundColor: ["#ef4444", "#facc15", "#3b82f6"],
        borderRadius: 10,
      },
    ],
  };

  const rombelKeys = Object.keys(perRombel);

  const chartPerRombel = {
    labels: rombelKeys.map((k) => perRombel[k].label),
    datasets: [
      {
        label: "Sakit",
        data: rombelKeys.map((k) => perRombel[k].sakit),
        backgroundColor: "#ef4444",
      },
      {
        label: "Izin",
        data: rombelKeys.map((k) => perRombel[k].izin),
        backgroundColor: "#facc15",
      },
      {
        label: "Tanpa Keterangan",
        data: rombelKeys.map((k) => perRombel[k].tanpa),
        backgroundColor: "#3b82f6",
      },
    ],
  };

  return (
    <div className="kepsek-container">
      <style>{`
        .kepsek-container {
          min-height: 100vh;
          background: #f3f4f6;
          padding: 16px;
        }
        @media (min-width: 640px) {
          .kepsek-container { padding: 24px; }
        }
        @media (min-width: 1024px) {
          .kepsek-container { padding: 40px; }
        }
        .card {
          background: white;
          border-radius: 20px;
          padding: 20px;
          box-shadow: 0 10px 25px rgba(0,0,0,.08);
        }
        .filter {
          display: grid;
          gap: 12px;
        }
        @media (min-width: 640px) {
          .filter { grid-template-columns: repeat(2,1fr); }
        }
        @media (min-width: 1024px) {
          .filter { grid-template-columns: repeat(3,1fr); }
        }
        .input {
          padding: 12px 16px;
          border-radius: 14px;
          border: 1px solid #d1d5db;
          width: 100%;
        }
        .summary {
          display: grid;
          gap: 12px;
        }
        @media (min-width: 640px) {
          .summary { grid-template-columns: repeat(3,1fr); }
        }
        .chart-box {
          height: 260px;
        }
        @media (min-width: 640px) {
          .chart-box { height: 340px; }
        }
        @media (min-width: 1024px) {
          .chart-box { height: 420px; }
        }
      `}</style>

      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold">Dashboard Absensi</h1>
        <p className="text-gray-600">Kepala Sekolah</p>
      </div>

      <div className="card filter max-w-5xl mx-auto mb-8">
        <input
          value={tahun}
          onChange={(e) => setTahun(e.target.value)}
          className="input"
        />
        <select
          value={semester}
          onChange={(e) => setSemester(e.target.value)}
          className="input"
        >
          <option value="">Semua Semester</option>
          <option value="1">Semester 1</option>
          <option value="2">Semester 2</option>
        </select>
        <select
          value={bulan}
          onChange={(e) => setBulan(e.target.value)}
          className="input"
        >
          {bulanList.map((b) => (
            <option key={b.value} value={b.value}>
              {b.label}
            </option>
          ))}
        </select>
      </div>

      <div className="summary max-w-5xl mx-auto mb-8">
        <div className="card text-center">
          Sakit
          <br />
          <b>{totalSekolah.sakit}</b>
        </div>
        <div className="card text-center">
          Izin
          <br />
          <b>{totalSekolah.izin}</b>
        </div>
        <div className="card text-center">
          Tanpa Keterangan
          <br />
          <b>{totalSekolah.tanpa}</b>
        </div>
      </div>

      <div className="card max-w-5xl mx-auto mb-8">
        <div className="chart-box">
          <Bar data={chartTotal} options={options} />
        </div>
      </div>

      <div className="card">
        <h2 className="text-xl font-bold mb-4 text-center">
          Rekap Absensi Per Rombel
        </h2>
        <div className="overflow-x-auto">
          <div className="chart-box min-w-[600px]">
            <Bar data={chartPerRombel} options={options} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileKepsek;
